import { useEffect, useRef, useState } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { Button } from '@/components/ui/button';
import { MapPin, Crosshair } from 'lucide-react';

interface LocationPickerProps {
  latitude?: number;
  longitude?: number;
  onLocationChange: (lat: number, lng: number) => void;
  disabled?: boolean;
}

// Default center: Riyadh
const DEFAULT_CENTER: [number, number] = [24.7136, 46.6753];
const DEFAULT_ZOOM = 12;

export function LocationPicker({
  latitude,
  longitude,
  onLocationChange,
  disabled = false,
}: LocationPickerProps) {
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<L.Map | null>(null);
  const markerRef = useRef<L.Marker | null>(null);
  const [isLocating, setIsLocating] = useState(false);

  // Custom marker icon
  const createIcon = () => {
    return L.divIcon({
      className: 'custom-warehouse-marker',
      html: `
        <div style="
          width: 40px;
          height: 40px;
          background: hsl(var(--primary));
          border-radius: 50% 50% 50% 0;
          transform: rotate(-45deg);
          display: flex;
          align-items: center;
          justify-content: center;
          box-shadow: 0 4px 12px rgba(0,0,0,0.3);
          border: 3px solid white;
        ">
          <svg 
            style="transform: rotate(45deg); width: 20px; height: 20px; color: white;"
            viewBox="0 0 24 24" 
            fill="none" 
            stroke="currentColor" 
            stroke-width="2"
          >
            <path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z"/>
            <circle cx="12" cy="10" r="3"/>
          </svg>
        </div>
      `,
      iconSize: [40, 40],
      iconAnchor: [20, 40],
    });
  };

  // Initialize map
  useEffect(() => {
    if (!mapRef.current || mapInstanceRef.current) return;

    const initialCenter: [number, number] = 
      latitude && longitude ? [latitude, longitude] : DEFAULT_CENTER;

    const map = L.map(mapRef.current, {
      zoomControl: false,
    }).setView(initialCenter, latitude && longitude ? 15 : DEFAULT_ZOOM);

    // Add zoom control to top-left
    L.control.zoom({ position: 'topleft' }).addTo(map);

    // Add tile layer
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '© OpenStreetMap',
    }).addTo(map);

    // Add marker if initial position exists
    if (latitude && longitude) {
      const marker = L.marker([latitude, longitude], {
        icon: createIcon(),
        draggable: !disabled,
      }).addTo(map);

      if (!disabled) {
        marker.on('dragend', () => {
          const pos = marker.getLatLng();
          onLocationChange(pos.lat, pos.lng);
        });
      }

      markerRef.current = marker;
    }

    // Click handler for placing marker
    if (!disabled) {
      map.on('click', (e: L.LeafletMouseEvent) => {
        const { lat, lng } = e.latlng;

        if (markerRef.current) {
          markerRef.current.setLatLng([lat, lng]);
        } else {
          const marker = L.marker([lat, lng], {
            icon: createIcon(),
            draggable: true,
          }).addTo(map);

          marker.on('dragend', () => {
            const pos = marker.getLatLng();
            onLocationChange(pos.lat, pos.lng);
          });

          markerRef.current = marker;
        }

        onLocationChange(lat, lng);
      });
    }

    mapInstanceRef.current = map;

    return () => {
      map.remove();
      mapInstanceRef.current = null;
      markerRef.current = null;
    };
  }, [disabled]);

  // Update marker when coordinates change externally
  useEffect(() => {
    if (!mapInstanceRef.current) return;

    if (latitude && longitude) {
      if (markerRef.current) {
        markerRef.current.setLatLng([latitude, longitude]);
      } else {
        const marker = L.marker([latitude, longitude], {
          icon: createIcon(),
          draggable: !disabled,
        }).addTo(mapInstanceRef.current);

        if (!disabled) {
          marker.on('dragend', () => {
            const pos = marker.getLatLng();
            onLocationChange(pos.lat, pos.lng);
          });
        }

        markerRef.current = marker;
      }
      mapInstanceRef.current.setView([latitude, longitude], 15);
    }
  }, [latitude, longitude, disabled]);

  // Get current location
  const handleGetCurrentLocation = () => {
    if (!navigator.geolocation) {
      return;
    }

    setIsLocating(true);

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude: lat, longitude: lng } = position.coords;
        onLocationChange(lat, lng);

        if (mapInstanceRef.current) {
          mapInstanceRef.current.setView([lat, lng], 15);
        }

        setIsLocating(false);
      },
      () => {
        setIsLocating(false);
      },
      { enableHighAccuracy: true }
    );
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <MapPin className="h-4 w-4" />
          <span>انقر على الخريطة لتحديد الموقع أو اسحب العلامة</span>
        </div>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={handleGetCurrentLocation}
          disabled={disabled || isLocating}
          className="gap-2"
        >
          <Crosshair className={`h-4 w-4 ${isLocating ? 'animate-spin' : ''}`} />
          {isLocating ? 'جاري التحديد...' : 'موقعي الحالي'}
        </Button>
      </div>

      <div 
        ref={mapRef} 
        className="h-[250px] w-full rounded-lg border bg-muted"
        style={{ zIndex: 1 }}
      />

      {latitude && longitude && (
        <div className="flex items-center gap-4 text-xs text-muted-foreground bg-muted/50 rounded-md p-2">
          <span dir="ltr">Lat: {latitude.toFixed(6)}</span>
          <span dir="ltr">Lng: {longitude.toFixed(6)}</span>
        </div>
      )}
    </div>
  );
}
